// ─── useOnboardingChat — chat-driven pre-subscription onboarding ──────
// Typed fetch wrappers for the EXISTING /api/onboarding-chat/* backend
// routes (ocn-S419-P0/S520-P0/S521-P0/S526-P0, owned by the ocn-cc lane —
// never modified from this file). Mirrors the authedFetch/asJson pattern
// established by useSubscriberChat.ts. The `state` object returned by
// every route is the SOURCE OF TRUTH for the onboarding step machine —
// callers must thread it back on every subsequent call unchanged except
// for what the server itself advances.
//
// ocn-S545-P0: frontend-only. Types below are a local mirror of the
// backend's OnboardingChatState/OfferingCopy shapes (apps/api/src/lib/
// onboarding-chat.ts) — never imported directly (that file lives outside
// this lane's edit scope and importing across the apps/api|apps/dashboard
// boundary is not how this monorepo wires cross-package types anyway).

import { authHeaders } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

// ocn-S545-P0: build-trigger flag. Read at CALL TIME (never cached at
// module scope) so tests can flip it per-case via vi.stubEnv before the
// decision point, and so a real deploy can flip the underlying env var
// without a rebuild-time constant baking in the old value.
export function isMaybeFireEnabled(): boolean {
  return import.meta.env.VITE_ONBOARDING_MAYBE_FIRE_ENABLED === 'true';
}

export type OfferingId = 'forge' | 'factory';

export interface OfferingCopy {
  id: OfferingId;
  name: string;
  monthlyPriceUsd: number;
  onboardingFeeUsd: number | null;
  includedMonthlyCredits: number;
  tagline: string;
  summary: string;
}

export type OnboardingChatStep =
  | 'qa'
  | 'offering_choice'
  | 'payment'
  | 'trial_explain'
  | 'app_idea'
  | 'credential_survey'
  | 'handoff';

export interface OnboardingChatState {
  step: OnboardingChatStep;
  offeringChoice: OfferingId | null;
  paymentConfirmed: boolean;
  surveyStarted: boolean;
}

export const initialOnboardingChatState = (): OnboardingChatState => ({
  step: 'qa',
  offeringChoice: null,
  paymentConfirmed: false,
  surveyStarted: false,
});

export interface StripeDiscount {
  promotion_code: string;
  coupon_id: string | null;
  name: string | null;
  percent_off: number | null;
  amount_off: number | null;
  currency: string | null;
}

export interface PaymentPrepareResponse {
  error?: string;
  pending_checkout?: { url: string; plan: string; billing_period: string; promotion_applied: boolean };
  offering: OfferingCopy;
  promoCode: string | null;
  stripe: {
    ready: boolean;
    priceId?: string | null;
    subscription_id?: string;
    client_secret?: string;
    customer_id?: string;
    discount?: StripeDiscount | null;
    note?: string;
  };
}

export interface MaybeFireResponse {
  ready: boolean;
  build_id?: string;
  already_enqueued?: boolean;
  missing?: string[];
  reason?: string;
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const auth = await authHeaders();
  return fetch(`${API_BASE}/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...auth,
      ...(init.headers as Record<string, string> | undefined),
    },
  });
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((body as { error?: string }).error || `API error: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const onboardingChatApi = {
  qa: async (message: string): Promise<{ reply: string; fallback?: boolean }> => {
    return asJson(await authedFetch('/onboarding-chat/qa', {
      method: 'POST',
      body: JSON.stringify({ message }),
    }));
  },

  choice: async (message: string, state: OnboardingChatState): Promise<{
    state: OnboardingChatState;
    resolved: boolean;
    reply?: string;
    offering?: OfferingCopy;
    handoffMode?: 'app_wizard' | 'sync_find_fix';
  }> => {
    return asJson(await authedFetch('/onboarding-chat/choice', {
      method: 'POST',
      body: JSON.stringify({ message, state }),
    }));
  },

  // ocn-S520-P0: forge returns stripe.ready=false (no live price configured
  // yet); factory returns a real client_secret to confirm via Stripe.js —
  // may resolve with stripe.ready=false + note on a genuine failure (HTTP
  // 400/500), which callers should render, not throw on.
  paymentPrepare: async (offering: OfferingId, promoCode?: string | null): Promise<PaymentPrepareResponse> => {
    const res = await authedFetch('/onboarding-chat/payment/prepare', {
      method: 'POST',
      body: JSON.stringify({ offering, ...(promoCode ? { promoCode } : {}) }),
    });
    const body = await res.json().catch(() => ({})) as PaymentPrepareResponse;
    return body;
  },

  trialExplain: async (state: OnboardingChatState): Promise<{ state: OnboardingChatState; message: string }> => {
    return asJson(await authedFetch('/onboarding-chat/trial-explain', {
      method: 'POST',
      body: JSON.stringify({ state }),
    }));
  },

  appIdea: async (idea: string, state: OnboardingChatState): Promise<{ state: OnboardingChatState; specSessionId: string }> => {
    return asJson(await authedFetch('/onboarding-chat/app-idea', {
      method: 'POST',
      body: JSON.stringify({ idea, state }),
    }));
  },

  survey: async (state: OnboardingChatState, projectId?: string | null): Promise<{
    state: OnboardingChatState;
    requirements: unknown[];
    githubInstallUrl: string | null;
  }> => {
    return asJson(await authedFetch('/onboarding-chat/survey', {
      method: 'POST',
      body: JSON.stringify({ state, ...(projectId ? { projectId } : {}) }),
    }));
  },

  surveyReadiness: async (
    state: OnboardingChatState,
    opts: { projectId?: string | null; specSessionId?: string | null } = {},
  ): Promise<{ state: OnboardingChatState; launchable: boolean; blockers: string[]; results: unknown[] }> => {
    return asJson(await authedFetch('/onboarding-chat/survey/readiness', {
      method: 'POST',
      body: JSON.stringify({
        state,
        ...(opts.projectId ? { projectId: opts.projectId } : {}),
        ...(opts.specSessionId ? { specSessionId: opts.specSessionId } : {}),
      }),
    }));
  },

  surveySubmit: async (params: {
    projectId?: string | null;
    service: string;
    keyName: string;
    value: string;
    module?: string;
  }): Promise<Record<string, unknown>> => {
    return asJson(await authedFetch('/onboarding-chat/survey/submit', {
      method: 'POST',
      body: JSON.stringify(params),
    }));
  },

  // ocn-S526-P0 item 4 / ocn-S545-P0: the build trigger. Kept behind
  // isMaybeFireEnabled() at the CALL SITE — this function itself always
  // makes the request when invoked (the gate belongs to the caller so it
  // is trivially unit-testable without network mocking games).
  maybeFire: async (specSessionId?: string | null): Promise<MaybeFireResponse> => {
    const res = await authedFetch('/onboarding-chat/factory/maybe-fire', {
      method: 'POST',
      body: JSON.stringify({ offering: 'factory', ...(specSessionId ? { specSessionId } : {}) }),
    });
    const body = await res.json().catch(() => ({ ready: false })) as MaybeFireResponse;
    return body;
  },
};
